/**
 * Governance Dashboard
 *
 * FERP CRITICAL: Centralized governance configuration for 5 governance primitives
 * Provides unified interface for escalation, scheduling, resources, integrations, and policies
 *
 * FERP Principles:
 * - Data-driven (all governance data from backend repositories)
 * - Repository pattern (NO direct GraphQL calls)
 * - Backend ID generation (useGenerateId for all entity IDs)
 * - Life-critical (immutable audit trail for all governance changes)
 * - Domain-agnostic (works across healthcare/finance/legal)
 *
 * 5 Governance Primitives:
 * 1. Escalation - Workflow failure escalation paths
 * 2. Schedule - Time-based workflow execution
 * 3. Resource - Compute/license/expert pool management
 * 4. Integration - External system health monitoring
 * 5. Policy - Business rule definitions
 */

import React, { useState, useEffect } from 'react';
import {
  Stack,
  Card,
  Group,
  Text,
  Badge,
  Button,
  Tabs,
  Grid,
  Paper,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  RingProgress,
  Center,
  Table,
  Alert,
  Modal,
  TextInput,
  Select,
  NumberInput,
  Switch,
  Textarea,
} from '@mantine/core';
import {
  IconSettings,
  IconAlertTriangle,
  IconClock,
  IconServer,
  IconPlug,
  IconShieldCheck,
  IconPlus,
  IconRefresh,
  IconDownload,
  IconUpload,
  IconInfoCircle,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { useGovernance } from './hooks/useGovernance';

/**
 * Governance Dashboard Component
 *
 * FERP Pattern: Centralized governance configuration with repository pattern
 */
export function GovernanceDashboard(): JSX.Element {
  const {
    escalationPolicies,
    workflowSchedules,
    resourceReservations,
    integrationConfigs,
    policyRules,
    isLoading,
    error,
    getEscalationPolicies,
    getWorkflowSchedules,
    getResourceReservations,
    getIntegrationConfigs,
    getPolicyRules,
    createEscalationPolicy,
    createWorkflowSchedule,
    createResourceReservation,
    triggerHealthCheck,
    dryRunPolicy,
  } = useGovernance();

  const [activeTab, setActiveTab] = useState<string>('escalation');
  const [createModalOpen, setCreateModalOpen] = useState(false);

  /**
   * Load all governance data on mount
   */
  useEffect(() => {
    getEscalationPolicies();
    getWorkflowSchedules();
    getResourceReservations();
    getIntegrationConfigs();
    getPolicyRules();
  }, [
    getEscalationPolicies,
    getWorkflowSchedules,
    getResourceReservations,
    getIntegrationConfigs,
    getPolicyRules,
  ]);

  /**
   * Refresh all governance data
   */
  const handleRefreshAll = (): void => {
    getEscalationPolicies();
    getWorkflowSchedules();
    getResourceReservations();
    getIntegrationConfigs();
    getPolicyRules();
  };

  /**
   * Render governance statistics
   */
  const renderStatistics = (): JSX.Element => {
    const totalEscalations = escalationPolicies.length;
    const activeEscalations = escalationPolicies.filter(p => p.enabled).length;
    const totalSchedules = workflowSchedules.length;
    const activeSchedules = workflowSchedules.filter(s => s.enabled).length;
    const totalResources = resourceReservations.length;
    const overUtilizedResources = resourceReservations.filter(
      r => (r.reserved / r.total_capacity) * 100 > r.utilization_threshold
    ).length;
    const totalIntegrations = integrationConfigs.length;
    const healthyIntegrations = integrationConfigs.filter(i => i.status === 'Healthy').length;
    const totalPolicies = policyRules.length;
    const activePolicies = policyRules.filter(p => p.enabled).length;

    return (
      <Grid gutter="md">
        <Grid.Col span={2.4}>
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="xs">
              <ThemeIcon color="orange" variant="light">
                <IconAlertTriangle size={20} />
              </ThemeIcon>
              <Text size="xs" color="dimmed">
                Escalation
              </Text>
            </Group>
            <Text size="xl" fw={700}>
              {activeEscalations}/{totalEscalations}
            </Text>
            <Text size="xs" color="dimmed">
              Active Policies
            </Text>
          </Paper>
        </Grid.Col>

        <Grid.Col span={2.4}>
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="xs">
              <ThemeIcon color="blue" variant="light">
                <IconClock size={20} />
              </ThemeIcon>
              <Text size="xs" color="dimmed">
                Schedule
              </Text>
            </Group>
            <Text size="xl" fw={700}>
              {activeSchedules}/{totalSchedules}
            </Text>
            <Text size="xs" color="dimmed">
              Active Schedules
            </Text>
          </Paper>
        </Grid.Col>

        <Grid.Col span={2.4}>
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="xs">
              <ThemeIcon color="green" variant="light">
                <IconServer size={20} />
              </ThemeIcon>
              <Text size="xs" color="dimmed">
                Resources
              </Text>
            </Group>
            <Text size="xl" fw={700} color={overUtilizedResources > 0 ? 'red' : undefined}>
              {totalResources}
            </Text>
            <Text size="xs" color="dimmed">
              {overUtilizedResources > 0 ? `${overUtilizedResources} Over Utilized` : 'All Normal'}
            </Text>
          </Paper>
        </Grid.Col>

        <Grid.Col span={2.4}>
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="xs">
              <ThemeIcon color="cyan" variant="light">
                <IconPlug size={20} />
              </ThemeIcon>
              <Text size="xs" color="dimmed">
                Integrations
              </Text>
            </Group>
            <Text
              size="xl"
              fw={700}
              c={healthyIntegrations < totalIntegrations ? 'orange' : 'green'}
            >
              {healthyIntegrations}/{totalIntegrations}
            </Text>
            <Text size="xs" color="dimmed">
              Healthy
            </Text>
          </Paper>
        </Grid.Col>

        <Grid.Col span={2.4}>
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="xs">
              <ThemeIcon color="violet" variant="light">
                <IconShieldCheck size={20} />
              </ThemeIcon>
              <Text size="xs" color="dimmed">
                Policies
              </Text>
            </Group>
            <Text size="xl" fw={700}>
              {activePolicies}/{totalPolicies}
            </Text>
            <Text size="xs" color="dimmed">
              Active Rules
            </Text>
          </Paper>
        </Grid.Col>
      </Grid>
    );
  };

  /**
   * Render escalation policies tab
   */
  const renderEscalationTab = (): JSX.Element => (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="lg" fw={600}>
          Escalation Policies
        </Text>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateModalOpen(true)}>
          Create Policy
        </Button>
      </Group>

      {escalationPolicies.length === 0 ? (
        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          No escalation policies configured. Create one to define workflow failure escalation
          paths.
        </Alert>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Workflow Type</th>
              <th>Levels</th>
              <th>Auto-Escalate</th>
              <th>Interval</th>
              <th>Fallback</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {escalationPolicies.map(policy => (
              <tr key={policy.escalation_id}>
                <td>{policy.workflow_type}</td>
                <td>
                  <Badge>{policy.escalation_levels.length} levels</Badge>
                </td>
                <td>
                  <ThemeIcon
                    size="sm"
                    color={policy.auto_escalate ? 'green' : 'gray'}
                    variant="light"
                  >
                    {policy.auto_escalate ? <IconCheck size={14} /> : <IconX size={14} />}
                  </ThemeIcon>
                </td>
                <td>{policy.escalation_interval_minutes} min</td>
                <td>
                  <Badge variant="outline">{policy.final_fallback_role}</Badge>
                </td>
                <td>
                  <Badge color={policy.enabled ? 'green' : 'gray'}>
                    {policy.enabled ? 'Active' : 'Disabled'}
                  </Badge>
                </td>
                <td>
                  <Group gap="xs">
                    <ActionIcon size="sm" variant="subtle">
                      <IconSettings size={14} />
                    </ActionIcon>
                  </Group>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Stack>
  );

  /**
   * Render workflow schedules tab
   */
  const renderScheduleTab = (): JSX.Element => (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="lg" fw={600}>
          Workflow Schedules
        </Text>
        <Button leftSection={<IconPlus size={16} />}>Create Schedule</Button>
      </Group>

      {workflowSchedules.length === 0 ? (
        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          No workflow schedules configured. Create one to automate workflow execution.
        </Alert>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Workflow</th>
              <th>Type</th>
              <th>Schedule</th>
              <th>Next Execution</th>
              <th>Timezone</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {workflowSchedules.map(schedule => (
              <tr key={schedule.schedule_id}>
                <td>{schedule.workflow_name}</td>
                <td>
                  <Badge>{schedule.schedule_type}</Badge>
                </td>
                <td>
                  {schedule.schedule_type === 'Cron' && schedule.cron_expression}
                  {schedule.schedule_type === 'Interval' && `${schedule.interval_minutes} min`}
                  {schedule.schedule_type === 'OneTime' &&
                    new Date(schedule.start_time).toLocaleString()}
                </td>
                <td>
                  {schedule.next_execution
                    ? new Date(schedule.next_execution).toLocaleString()
                    : 'N/A'}
                </td>
                <td>{schedule.timezone}</td>
                <td>
                  <Badge color={schedule.enabled ? 'green' : 'gray'}>
                    {schedule.enabled ? 'Active' : 'Disabled'}
                  </Badge>
                </td>
                <td>
                  <Group gap="xs">
                    <ActionIcon size="sm" variant="subtle">
                      <IconSettings size={14} />
                    </ActionIcon>
                  </Group>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Stack>
  );

  /**
   * Render resource reservations tab
   */
  const renderResourceTab = (): JSX.Element => (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="lg" fw={600}>
          Resource Pools
        </Text>
        <Button leftSection={<IconPlus size={16} />}>Add Resource</Button>
      </Group>

      {resourceReservations.length === 0 ? (
        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          No resource pools configured. Create one to manage compute, licenses, or expert capacity.
        </Alert>
      ) : (
        <Grid>
          {resourceReservations.map(resource => {
            const utilizationPct = (resource.reserved / resource.total_capacity) * 100;
            const isOverUtilized = utilizationPct > resource.utilization_threshold;

            return (
              <Grid.Col key={resource.resource_id} span={4}>
                <Card withBorder>
                  <Group justify="space-between" mb="md">
                    <div>
                      <Text size="sm" fw={500}>
                        {resource.resource_name}
                      </Text>
                      <Badge size="sm" variant="outline">
                        {resource.resource_type}
                      </Badge>
                    </div>
                    <Center>
                      <RingProgress
                        size={80}
                        thickness={8}
                        sections={[
                          {
                            value: utilizationPct,
                            color: isOverUtilized ? 'red' : 'blue',
                          },
                        ]}
                        label={
                          <Center>
                            <Text size="xs" fw={700}>
                              {utilizationPct.toFixed(0)}%
                            </Text>
                          </Center>
                        }
                      />
                    </Center>
                  </Group>

                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="xs" color="dimmed">
                        Total Capacity
                      </Text>
                      <Text size="sm" fw={500}>
                        {resource.total_capacity}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="xs" color="dimmed">
                        Reserved
                      </Text>
                      <Text size="sm" fw={500}>
                        {resource.reserved}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="xs" color="dimmed">
                        Available
                      </Text>
                      <Text size="sm" fw={500} color={resource.available > 0 ? 'green' : 'red'}>
                        {resource.available}
                      </Text>
                    </Group>
                  </Stack>

                  {isOverUtilized && (
                    <Alert icon={<IconAlertTriangle size={14} />} color="red" mt="sm">
                      <Text size="xs">
                        Utilization above {resource.utilization_threshold}% threshold
                      </Text>
                    </Alert>
                  )}
                </Card>
              </Grid.Col>
            );
          })}
        </Grid>
      )}
    </Stack>
  );

  /**
   * Render integration configs tab
   */
  const renderIntegrationTab = (): JSX.Element => (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="lg" fw={600}>
          External Integrations
        </Text>
        <Button leftSection={<IconPlus size={16} />}>Add Integration</Button>
      </Group>

      {integrationConfigs.length === 0 ? (
        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          No integrations configured. Add external systems to monitor their health status.
        </Alert>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Service</th>
              <th>Endpoint</th>
              <th>Auth Type</th>
              <th>Health Check</th>
              <th>Response Time</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {integrationConfigs.map(integration => {
              const statusColor =
                integration.status === 'Healthy'
                  ? 'green'
                  : integration.status === 'Degraded'
                  ? 'orange'
                  : 'red';

              return (
                <tr key={integration.integration_id}>
                  <td>{integration.service_name}</td>
                  <td>
                    <Text size="sm" style={{ maxWidth: 200, overflow: 'hidden' }}>
                      {integration.endpoint_url}
                    </Text>
                  </td>
                  <td>
                    <Badge variant="outline">{integration.auth_type}</Badge>
                  </td>
                  <td>
                    <Text size="xs" color="dimmed">
                      {integration.last_health_check
                        ? new Date(integration.last_health_check).toLocaleString()
                        : 'Never'}
                    </Text>
                  </td>
                  <td>
                    {integration.response_time_ms ? `${integration.response_time_ms}ms` : 'N/A'}
                  </td>
                  <td>
                    <Badge color={statusColor}>{integration.status}</Badge>
                  </td>
                  <td>
                    <Group gap="xs">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={() => triggerHealthCheck(integration.integration_id)}
                      >
                        <IconRefresh size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle">
                        <IconSettings size={14} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Stack>
  );

  /**
   * Render policy rules tab
   */
  const renderPolicyTab = (): JSX.Element => (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="lg" fw={600}>
          Policy Rules
        </Text>
        <Button leftSection={<IconPlus size={16} />}>Create Policy</Button>
      </Group>

      {policyRules.length === 0 ? (
        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          No policy rules configured. Create business rules with dry-run testing capability.
        </Alert>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Policy Name</th>
              <th>Type</th>
              <th>Conditions</th>
              <th>Actions</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {policyRules.map(policy => (
              <tr key={policy.policy_id}>
                <td>{policy.policy_name}</td>
                <td>
                  <Badge>{policy.policy_type}</Badge>
                </td>
                <td>
                  <Badge variant="outline">{policy.conditions.length} conditions</Badge>
                </td>
                <td>
                  <Badge variant="outline">{policy.actions.length} actions</Badge>
                </td>
                <td>{policy.priority}</td>
                <td>
                  <Badge color={policy.enabled ? 'green' : 'gray'}>
                    {policy.enabled ? 'Active' : 'Disabled'}
                  </Badge>
                </td>
                <td>
                  <Group gap="xs">
                    <Tooltip label="Dry Run Test">
                      <ActionIcon size="sm" variant="subtle">
                        <IconShieldCheck size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <ActionIcon size="sm" variant="subtle">
                      <IconSettings size={14} />
                    </ActionIcon>
                  </Group>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Stack>
  );

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <ThemeIcon color="blue">
              <IconSettings size={24} />
            </ThemeIcon>
            <div>
              <Text size="xl" fw={700}>
                Governance Dashboard
              </Text>
              <Text size="sm" color="dimmed">
                Centralized configuration for 5 governance primitives
              </Text>
            </div>
          </Group>
          <Group gap="xs">
            <Tooltip label="Refresh All">
              <ActionIcon onClick={handleRefreshAll} loading={isLoading}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Import Config">
              <ActionIcon>
                <IconUpload size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Export Config">
              <ActionIcon>
                <IconDownload size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {renderStatistics()}
      </Card>

      {error && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" onClose={() => {}}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value ?? 'escalation')}>
        <Tabs.List>
          <Tabs.Tab value="escalation" leftSection={<IconAlertTriangle size={14} />}>
            Escalation
          </Tabs.Tab>
          <Tabs.Tab value="schedule" leftSection={<IconClock size={14} />}>
            Schedule
          </Tabs.Tab>
          <Tabs.Tab value="resource" leftSection={<IconServer size={14} />}>
            Resources
          </Tabs.Tab>
          <Tabs.Tab value="integration" leftSection={<IconPlug size={14} />}>
            Integrations
          </Tabs.Tab>
          <Tabs.Tab value="policy" leftSection={<IconShieldCheck size={14} />}>
            Policies
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="escalation" pt="md">
          {renderEscalationTab()}
        </Tabs.Panel>

        <Tabs.Panel value="schedule" pt="md">
          {renderScheduleTab()}
        </Tabs.Panel>

        <Tabs.Panel value="resource" pt="md">
          {renderResourceTab()}
        </Tabs.Panel>

        <Tabs.Panel value="integration" pt="md">
          {renderIntegrationTab()}
        </Tabs.Panel>

        <Tabs.Panel value="policy" pt="md">
          {renderPolicyTab()}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
